import { Cloud, CloudRain, CloudSun, type LucideIcon, Sun } from "lucide-react";
import { useThemeColor } from "@/hooks/use-theme-color";

export interface QuickActionCardProps {
	icon: LucideIcon;
	title: string;
	subtitle: string;
	onClick?: () => void;
}

export function QuickActionCard({
	icon: Icon,
	title,
	subtitle,
	onClick,
}: QuickActionCardProps) {
	return (
		<button
			type="button"
			className="group relative cursor-pointer overflow-hidden rounded-[20px] border border-dashboard-border p-5 text-left transition-all duration-300 hover:-translate-y-1 dashboard-quick-action-card flex flex-col items-start justify-start h-full"
			onClick={onClick}
		>
			<div className="dashboard-quick-action-icon flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300">
				<Icon className="h-5 w-5 transition-colors duration-300 text-dashboard-accent-dark group-hover:text-dashboard-on-fill" />
			</div>

			<h3 className="mt-6 font-display text-[20px] italic font-medium leading-none tracking-[-0.03em] text-dashboard-text">
				{title}
			</h3>

			<p className="mt-2 text-[14px] text-dashboard-muted">{subtitle}</p>
		</button>
	);
}

export interface ProgressRingProps {
	percentage: number;
	size?: number;
	strokeWidth?: number;
}

export function ProgressRing({
	percentage,
	size = 108,
	strokeWidth = 6,
}: ProgressRingProps) {
	const radius = (size - strokeWidth) / 2;
	const circumference = radius * 2 * Math.PI;
	// The arc is drawn within 0-100 and the number is printed as given. Past 100
	// the offset goes negative and the dash pattern wraps into its gap, drawing
	// less of the ring than 100 does; `computeBudget`'s percentage exceeds 100
	// when a contractor is paid past the funding.
	const drawn = Math.min(100, Math.max(0, percentage));
	// SVG stop colors are attributes; see useThemeColor.
	const trackFrom = useThemeColor("--color-dashboard-divider", "#F1F5F9");
	const trackTo = useThemeColor("--color-dashboard-border", "#E2E8F0");
	const offset = circumference - (drawn / 100) * circumference;

	return (
		<div className="relative" style={{ width: size, height: size }}>
			<svg
				width={size}
				height={size}
				style={{ transform: "rotate(-90deg)" }}
				aria-hidden="true"
			>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="url(#ringTrack)"
					strokeWidth={strokeWidth}
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="url(#ringFill)"
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					style={{ filter: "url(#ringGlow)" }}
				/>
				<defs>
					<linearGradient id="ringTrack" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={trackFrom} />
						<stop offset="100%" stopColor={trackTo} />
					</linearGradient>
					<linearGradient id="ringFill" x1="0" y1="0" x2="1" y2="1">
						<stop
							offset="0%"
							style={{ stopColor: "var(--color-dashboard-progress-complete)" }}
						/>
						<stop
							offset="50%"
							style={{ stopColor: "var(--color-dashboard-progress-complete)" }}
						/>
						<stop
							offset="100%"
							style={{ stopColor: "var(--color-dashboard-accent)" }}
						/>
					</linearGradient>
					<filter id="ringGlow" x="-20%" y="-20%" width="140%" height="140%">
						<feGaussianBlur stdDeviation="2.2" result="blur" />
						<feMerge>
							<feMergeNode in="blur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>
			</svg>
			<div
				className="absolute inset-3 rounded-full flex flex-col items-center justify-center"
				style={{
					background:
						"linear-gradient(135deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 55%, var(--color-dashboard-divider) 100%)",
					boxShadow:
						"color-mix(in srgb, var(--color-dashboard-paper) 95%, transparent) 0px 1px 0px inset, color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent) 0px -2px 6px inset",
				}}
			>
				<span className="font-display text-2xl font-medium text-dashboard-text tabular-nums">
					{percentage}
					<span className="text-sm text-dashboard-accent-dark">%</span>
				</span>
				<span className="font-mono text-[7.5px] font-bold uppercase tracking-widest text-dashboard-muted mt-1">
					Spent
				</span>
			</div>
		</div>
	);
}

export type WeatherType = "sun" | "cloud" | "rain" | "cloudsun";

export interface WeatherIconProps {
	type: WeatherType;
}

export function WeatherIcon({ type }: WeatherIconProps) {
	const iconClass = "w-5 h-5";
	switch (type) {
		case "sun":
			return <Sun className={iconClass} />;
		case "cloud":
			return <Cloud className={iconClass} />;
		case "rain":
			return <CloudRain className={iconClass} />;
		case "cloudsun":
			return <CloudSun className={iconClass} />;
	}
}

export interface RoomCardProps {
	name: string;
	status: string;
	progress: number;
	image?: string;
	onClick?: () => void;
}

export function RoomCard({
	name,
	status,
	progress,
	image,
	onClick,
}: RoomCardProps) {
	return (
		<button
			type="button"
			className="group relative aspect-4/5 overflow-hidden rounded-xl border-0 bg-none p-0 shadow-md transition-all duration-350 hover:shadow-xl hover:-translate-y-1 dashboard-room-card"
			onClick={onClick}
		>
			{image ? (
				<img
					src={image}
					alt={name}
					className="absolute inset-0 h-full w-full object-cover transition-transform duration-1200 group-hover:scale-105 filter-warm"
				/>
			) : (
				<div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(135deg, var(--color-dashboard-warm-100) 0%, var(--color-dashboard-warm-200) 100%)",
					}}
				/>
			)}
			<div className="absolute inset-0 dashboard-overlay-bottom" />
			<div className="absolute right-2 top-2">
				<div className="relative w-10 h-10">
					<svg
						width="40"
						height="40"
						viewBox="0 0 40 40"
						style={{ transform: "rotate(-90deg)" }}
						aria-hidden="true"
					>
						<circle
							cx="20"
							cy="20"
							r="16"
							fill="none"
							stroke="rgba(255,255,255,0.22)"
							strokeWidth="3"
						/>
						<circle
							cx="20"
							cy="20"
							r="16"
							fill="none"
							style={{ stroke: "var(--color-dashboard-on-photo-warm)" }}
							strokeWidth="3"
							strokeLinecap="round"
							strokeDasharray="100.53"
							strokeDashoffset={(100.53 * (100 - progress)) / 100}
						/>
					</svg>
					<div className="absolute inset-0 flex items-center justify-center font-mono text-[9.5px] font-bold text-dashboard-on-fill">
						{progress}
					</div>
				</div>
			</div>
			<div className="absolute bottom-3 left-3 right-3 text-left text-dashboard-on-fill">
				<span className="font-display text-lg font-medium italic">{name}.</span>
				<p className="mt-1 font-mono text-[8.5px] uppercase tracking-wider opacity-75">
					{status}
				</p>
			</div>
		</button>
	);
}
